import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import useConfigurators from "../../api/configurator";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  SlidersHorizontal,
  Plus,
  Pencil,
  Trash2,
  Search,
  Eye,
  Copy,
  Globe,
  FileEdit,
  Layers,
  ExternalLink,
  Inbox,
  Mail,
  Phone,
  User,
  Clock,
  ChevronDown,
  X,
  CheckCircle2,
  MessageSquare,
  ArrowLeft,
} from "lucide-react";

const S3_BASE_URL = process.env.REACT_APP_S3_BASE_URL;

const getImageUrl = (imageKey) => {
  if (!imageKey) return null;
  if (imageKey.startsWith("http") || imageKey.startsWith("data:"))
    return imageKey;
  return `${S3_BASE_URL}/${imageKey}`;
};

const ConfiguratorManager = ({ targetCompanyId }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const {
    getAllConfigurators,
    deleteConfigurator,
    getConfiguratorsByCompanyId,
    deleteConfiguratorForCompany,
    getConfiguratorRequests,
    updateConfiguratorRequestStatus,
    deleteConfiguratorRequest,
    getConfiguratorRequestStats,
    getConfiguratorRequestsByCompany,
    getConfiguratorRequestStatsByCompany,
    updateConfiguratorRequestStatusByCompany,
    deleteConfiguratorRequestByCompany,
  } = useConfigurators();
  const [configurators, setConfigurators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("configurators");
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestStats, setRequestStats] = useState({ total: 0, new: 0 });
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { type: 'configurator'|'request', id, name }

  const isAdmin = !!targetCompanyId;

  useEffect(() => {
    fetchConfigurators();
  }, [targetCompanyId]);

  const fetchConfigurators = async () => {
    try {
      const data = isAdmin
        ? await getConfiguratorsByCompanyId(targetCompanyId)
        : await getAllConfigurators();
      setConfigurators(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching configurators:", error);
      setConfigurators([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      setRequestsLoading(true);
      const data = isAdmin
        ? await getConfiguratorRequestsByCompany(targetCompanyId)
        : await getConfiguratorRequests();
      setRequests(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching requests:", error);
      setRequests([]);
    } finally {
      setRequestsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const stats = isAdmin
        ? await getConfiguratorRequestStatsByCompany(targetCompanyId)
        : await getConfiguratorRequestStats();
      setRequestStats(stats);
    } catch (error) {
      console.error("Error fetching request stats:", error);
    }
  };

  useEffect(() => {
    if (activeTab === "requests") {
      fetchRequests();
    }
  }, [activeTab]);

  useEffect(() => {
    fetchStats();
  }, []);

  const handleDelete = (id, name) => {
    setDeleteConfirm({ type: "configurator", id, name });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      if (deleteConfirm.type === "configurator") {
        if (isAdmin) {
          await deleteConfiguratorForCompany(targetCompanyId, deleteConfirm.id);
        } else {
          await deleteConfigurator(deleteConfirm.id);
        }
        fetchConfigurators();
      } else {
        if (isAdmin) {
          await deleteConfiguratorRequestByCompany(
            targetCompanyId,
            deleteConfirm.id,
          );
        } else {
          await deleteConfiguratorRequest(deleteConfirm.id);
        }
        fetchRequests();
        fetchStats();
        if (selectedRequest?.id === deleteConfirm.id) setSelectedRequest(null);
      }
    } catch (error) {
      console.error("Error deleting:", error);
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleDuplicate = async () => {
    // Future: implement duplicate functionality
    alert("Dupliceren komt binnenkort beschikbaar!");
  };

  const handleDeleteRequest = (id, name) => {
    setDeleteConfirm({ type: "request", id, name });
  };

  const handleStatusChange = async (id, status) => {
    try {
      const updated = isAdmin
        ? await updateConfiguratorRequestStatusByCompany(
            targetCompanyId,
            id,
            status,
          )
        : await updateConfiguratorRequestStatus(id, status);
      setRequests((prev) => prev.map((r) => (r.id === id ? updated : r)));
      if (selectedRequest?.id === id) setSelectedRequest(updated);
      fetchStats();
    } catch (error) {
      console.error("Error updating request status:", error);
    }
  };

  const statusConfig = {
    new: { label: "Nieuw", className: "bg-blue-100 text-blue-700" },
    read: { label: "Gelezen", className: "bg-gray-100 text-gray-700" },
    contacted: {
      label: "Gecontacteerd",
      className: "bg-amber-100 text-amber-700",
    },
    completed: { label: "Afgerond", className: "bg-green-100 text-green-700" },
    archived: {
      label: "Gearchiveerd",
      className: "bg-neutral-100 text-neutral-500",
    },
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("nl-BE", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const filteredConfigurators = configurators.filter((c) =>
    (c.name || "").toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const getStepCount = (configurator) => {
    try {
      const steps = configurator.steps || [];
      return Array.isArray(steps) ? steps.length : 0;
    } catch {
      return 0;
    }
  };

  const getOptionCount = (configurator) => {
    try {
      const steps = configurator.steps || [];
      if (!Array.isArray(steps)) return 0;
      return steps.reduce((total, step) => {
        return total + (Array.isArray(step.options) ? step.options.length : 0);
      }, 0);
    } catch {
      return 0;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div
          className={`w-8 h-8 rounded-full border-2 animate-spin ${isDark ? "border-white/[0.08] border-t-white/40" : "border-gray-200 border-t-gray-600"}`}
        />
      </div>
    );
  }

  return (
    <>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirm}
        onOpenChange={(open) => !open && setDeleteConfirm(null)}
      >
        <DialogContent
          className={`sm:max-w-md ${
            isDark ? "bg-[#111] border-white/[0.08] text-white" : "bg-white"
          }`}
        >
          <DialogHeader>
            <DialogTitle className={isDark ? "text-white" : "text-gray-900"}>
              {deleteConfirm?.type === "configurator"
                ? "Configurator verwijderen"
                : "Aanvraag verwijderen"}
            </DialogTitle>
            <DialogDescription
              className={isDark ? "text-gray-400" : "text-gray-500"}
            >
              {deleteConfirm?.type === "configurator" ? (
                <>
                  Ben je zeker dat je{" "}
                  <span
                    className={`font-semibold ${
                      isDark ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {deleteConfirm?.name}
                  </span>{" "}
                  wilt verwijderen? Dit kan niet ongedaan worden.
                </>
              ) : (
                <>
                  Ben je zeker dat je de aanvraag van{" "}
                  <span
                    className={`font-semibold ${
                      isDark ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {deleteConfirm?.name}
                  </span>{" "}
                  wilt verwijderen?
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirm(null)}
              className={
                isDark
                  ? "border-white/[0.08] text-gray-300 hover:bg-white/[0.04]"
                  : ""
              }
            >
              Annuleren
            </Button>
            <Button
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white border-0"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Verwijderen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="space-y-6 max-w-7xl mx-auto pb-20">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1
              className={`text-2xl font-semibold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
            >
              Configurators
            </h1>
            <p
              className={`mt-1 text-sm ${isDark ? "text-gray-500" : "text-gray-500"}`}
            >
              Bouw interactieve product configurators voor je klanten
            </p>
          </div>
          <button
            onClick={() =>
              navigate(
                isAdmin
                  ? `/dashboard/admin-configurators/${targetCompanyId}/new`
                  : "/dashboard/configurator/new",
              )
            }
            className={`h-9 px-4 rounded-lg text-[13px] font-medium inline-flex items-center gap-2 transition-colors ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
          >
            <Plus className="h-3.5 w-3.5" />
            Nieuwe Configurator
          </button>
        </div>

        {/* Tabs */}
        <div
          className={`flex gap-1 rounded-lg p-1 w-fit ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
        >
          <button
            onClick={() => setActiveTab("configurators")}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
              activeTab === "configurators"
                ? isDark
                  ? "bg-white/[0.1] text-white shadow-sm"
                  : "bg-white text-gray-900 shadow-sm"
                : isDark
                  ? "text-gray-400 hover:text-white"
                  : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <SlidersHorizontal className="h-4 w-4 inline mr-2" />
            Configurators
          </button>
          <button
            onClick={() => setActiveTab("requests")}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2 ${
              activeTab === "requests"
                ? isDark
                  ? "bg-white/[0.1] text-white shadow-sm"
                  : "bg-white text-gray-900 shadow-sm"
                : isDark
                  ? "text-gray-400 hover:text-white"
                  : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Inbox className="h-4 w-4" />
            Aanvragen
            {requestStats.new > 0 && (
              <span className="bg-blue-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                {requestStats.new}
              </span>
            )}
          </button>
        </div>

        {activeTab === "configurators" ? (
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div
                className={`rounded-xl p-3 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
              >
                <div className="flex items-center gap-1.5 mb-1.5">
                  <SlidersHorizontal
                    className={`h-3 w-3 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                  />
                  <span
                    className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Totaal
                  </span>
                </div>
                <span
                  className={`text-xl font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  {configurators.length}
                </span>
              </div>
              <div
                className={`rounded-xl p-3 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
              >
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Globe
                    className={`h-3 w-3 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                  />
                  <span
                    className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Gepubliceerd
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className={`h-2 w-2 rounded-full ${isDark ? "bg-emerald-400" : "bg-emerald-500"}`}
                  />
                  <span
                    className={`text-xl font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    {
                      configurators.filter((c) => c.status === "published")
                        .length
                    }
                  </span>
                </div>
              </div>
              <div
                className={`rounded-xl p-3 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
              >
                <div className="flex items-center gap-1.5 mb-1.5">
                  <FileEdit
                    className={`h-3 w-3 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                  />
                  <span
                    className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Concepten
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className={`h-2 w-2 rounded-full ${isDark ? "bg-amber-400" : "bg-amber-500"}`}
                  />
                  <span
                    className={`text-xl font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    {configurators.filter((c) => c.status === "draft").length}
                  </span>
                </div>
              </div>
            </div>

            {/* Mobile Search Block */}
            <div
              className={`sm:hidden rounded-xl border p-4 ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-100"}`}
            >
              <div className="relative w-full">
                <Search
                  className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                />
                <Input
                  placeholder="Zoeken..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`pl-9 ${isDark ? "border-white/[0.1] bg-white/[0.03] text-white placeholder:text-gray-500" : "border-gray-200"}`}
                />
              </div>
            </div>

            {/* Mobile Cards */}
            {filteredConfigurators.length === 0 ? (
              <div
                className={`sm:hidden rounded-xl border p-12 text-center ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-100"}`}
              >
                <div
                  className={`mx-auto h-12 w-12 rounded-full flex items-center justify-center mb-3 ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
                >
                  <SlidersHorizontal
                    className={`h-6 w-6 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                  />
                </div>
                <h3
                  className={`text-[14px] font-semibold mb-1 ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  {searchTerm ? "Geen resultaten" : "Nog geen configurators"}
                </h3>
                <p
                  className={`text-[13px] max-w-md mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}
                >
                  {searchTerm
                    ? "Probeer een andere zoekterm"
                    : "Maak je eerste configurator aan."}
                </p>
              </div>
            ) : (
              <div className="sm:hidden space-y-3">
                {filteredConfigurators.map((configurator, i) => (
                  <motion.div
                    key={configurator.id}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.02 }}
                    className={`rounded-xl overflow-hidden cursor-pointer ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
                    onClick={() =>
                      navigate(
                        isAdmin
                          ? `/dashboard/admin-configurators/${targetCompanyId}/${configurator.id}`
                          : `/dashboard/configurator/${configurator.id}`,
                      )
                    }
                  >
                    <div className="p-3 flex items-center gap-3">
                      {configurator.image ? (
                        <img
                          src={getImageUrl(configurator.image)}
                          alt={configurator.name}
                          className="h-11 w-11 rounded-lg object-cover flex-shrink-0"
                        />
                      ) : (
                        <div
                          className={`h-11 w-11 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
                        >
                          <SlidersHorizontal
                            className={`h-5 w-5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3
                            className={`text-[13px] font-semibold truncate ${isDark ? "text-white" : "text-gray-900"}`}
                          >
                            {configurator.name}
                          </h3>
                          <Badge
                            className={`flex-shrink-0 text-[11px] ${configurator.status === "published" ? (isDark ? "bg-green-500/15 text-green-400 hover:bg-green-500/15" : "bg-green-100 text-green-700 hover:bg-green-100") : isDark ? "bg-amber-500/15 text-amber-400 hover:bg-amber-500/15" : "bg-amber-100 text-amber-700 hover:bg-amber-100"}`}
                          >
                            {configurator.status === "published"
                              ? "Actief"
                              : "Concept"}
                          </Badge>
                        </div>
                        <span
                          className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                        >
                          {getStepCount(configurator)} stappen ·{" "}
                          {getOptionCount(configurator)} opties
                        </span>
                      </div>
                      <div
                        className="flex items-center gap-1 flex-shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() =>
                            navigate(
                              isAdmin
                                ? `/dashboard/admin-configurators/${targetCompanyId}/${configurator.id}`
                                : `/dashboard/configurator/${configurator.id}`,
                            )
                          }
                          className={`h-8 w-8 rounded-lg inline-flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() =>
                            handleDelete(configurator.id, configurator.name)
                          }
                          className={`h-8 w-8 rounded-lg inline-flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-red-400 hover:bg-red-500/10" : "text-gray-400 hover:text-red-600 hover:bg-red-50"}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Desktop: Search & Table in one wrapper */}
            <div
              className={`hidden sm:block rounded-xl border ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
            >
              <div className="p-6 pb-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3
                      className={`text-[15px] font-semibold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
                    >
                      Alle Configurators
                    </h3>
                    <p
                      className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Beheer en bewerk je product configurators
                    </p>
                  </div>
                  <div className="relative w-64">
                    <Search
                      className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                    />
                    <Input
                      placeholder="Zoeken..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={`pl-9 ${isDark ? "border-white/[0.1] bg-white/[0.03] text-white placeholder:text-gray-500" : "border-gray-200"}`}
                    />
                  </div>
                </div>
              </div>
              <div className="px-6 pb-6">
                {filteredConfigurators.length === 0 ? (
                  <div className="text-center py-16">
                    <div
                      className={`mx-auto h-16 w-16 rounded-full flex items-center justify-center mb-4 ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
                    >
                      <SlidersHorizontal
                        className={`h-8 w-8 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                      />
                    </div>
                    <h3
                      className={`text-lg font-medium mb-2 ${isDark ? "text-white" : "text-gray-900"}`}
                    >
                      {searchTerm
                        ? "Geen resultaten gevonden"
                        : "Nog geen configurators"}
                    </h3>
                    <p
                      className={`mb-6 max-w-md mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}
                    >
                      {searchTerm
                        ? "Probeer een andere zoekterm"
                        : "Maak je eerste configurator aan om je klanten een interactieve productbeleving te bieden."}
                    </p>
                    {!searchTerm && (
                      <Button
                        onClick={() =>
                          navigate(
                            isAdmin
                              ? `/dashboard/admin-configurators/${targetCompanyId}/new`
                              : "/dashboard/configurator/new",
                          )
                        }
                        className={`rounded-lg h-9 px-4 text-[13px] font-medium ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Eerste Configurator Aanmaken
                      </Button>
                    )}
                  </div>
                ) : (
                  <div
                    className={`rounded-md border overflow-hidden ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
                  >
                    <Table>
                      <TableHeader>
                        <TableRow
                          className={
                            isDark
                              ? "bg-white/[0.03] border-white/[0.06]"
                              : "bg-gray-50/50"
                          }
                        >
                          <TableHead
                            className={`font-semibold ${isDark ? "text-gray-400" : "text-gray-700"}`}
                          >
                            Naam
                          </TableHead>
                          <TableHead
                            className={`font-semibold ${isDark ? "text-gray-400" : "text-gray-700"}`}
                          >
                            Status
                          </TableHead>
                          <TableHead
                            className={`font-semibold text-center ${isDark ? "text-gray-400" : "text-gray-700"}`}
                          >
                            Stappen
                          </TableHead>
                          <TableHead
                            className={`font-semibold text-center ${isDark ? "text-gray-400" : "text-gray-700"}`}
                          >
                            Opties
                          </TableHead>
                          <TableHead
                            className={`font-semibold text-right ${isDark ? "text-gray-400" : "text-gray-700"}`}
                          >
                            Acties
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredConfigurators.map((configurator) => (
                          <TableRow
                            key={configurator.id}
                            className={`cursor-pointer ${isDark ? "hover:bg-white/[0.03] border-white/[0.06]" : "hover:bg-gray-50/50"}`}
                            onClick={() =>
                              navigate(
                                isAdmin
                                  ? `/dashboard/admin-configurators/${targetCompanyId}/${configurator.id}`
                                  : `/dashboard/configurator/${configurator.id}`,
                              )
                            }
                          >
                            <TableCell>
                              <div className="flex items-center space-x-3">
                                {configurator.image ? (
                                  <img
                                    src={getImageUrl(configurator.image)}
                                    alt={configurator.name}
                                    className="h-10 w-10 rounded-lg object-cover"
                                  />
                                ) : (
                                  <div
                                    className={`h-10 w-10 rounded-lg flex items-center justify-center ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
                                  >
                                    <SlidersHorizontal
                                      className={`h-5 w-5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                                    />
                                  </div>
                                )}
                                <div>
                                  <p
                                    className={`font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                                  >
                                    {configurator.name}
                                  </p>
                                  {configurator.description && (
                                    <p
                                      className={`text-sm truncate max-w-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
                                    >
                                      {configurator.description}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge
                                className={
                                  configurator.status === "published"
                                    ? isDark
                                      ? "bg-green-500/15 text-green-400 hover:bg-green-500/15"
                                      : "bg-green-100 text-green-700 hover:bg-green-100"
                                    : isDark
                                      ? "bg-amber-500/15 text-amber-400 hover:bg-amber-500/15"
                                      : "bg-amber-100 text-amber-700 hover:bg-amber-100"
                                }
                              >
                                {configurator.status === "published"
                                  ? "Gepubliceerd"
                                  : "Concept"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center space-x-1">
                                <Layers
                                  className={`h-4 w-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                                />
                                <span
                                  className={
                                    isDark ? "text-gray-300" : "text-gray-700"
                                  }
                                >
                                  {getStepCount(configurator)}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell
                              className={`text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}
                            >
                              {getOptionCount(configurator)}
                            </TableCell>
                            <TableCell className="text-right">
                              <div
                                className="flex items-center justify-end space-x-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className={`h-8 w-8 ${isDark ? "text-gray-400 hover:text-white hover:bg-white/[0.06]" : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"}`}
                                  onClick={() =>
                                    navigate(
                                      isAdmin
                                        ? `/dashboard/admin-configurators/${targetCompanyId}/${configurator.id}`
                                        : `/dashboard/configurator/${configurator.id}`,
                                    )
                                  }
                                  title="Bewerken"
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className={`h-8 w-8 ${isDark ? "text-gray-400 hover:text-red-400 hover:bg-red-500/10" : "text-gray-500 hover:text-red-600 hover:bg-red-50"}`}
                                  onClick={() =>
                                    handleDelete(
                                      configurator.id,
                                      configurator.name,
                                    )
                                  }
                                  title="Verwijderen"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          /* ═══════════ REQUESTS TAB ═══════════ */
          <>
            {/* Request Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div
                className={`rounded-xl p-3 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
              >
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Inbox
                    className={`h-3 w-3 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                  />
                  <span
                    className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Totaal
                  </span>
                </div>
                <span
                  className={`text-xl font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  {requestStats.total}
                </span>
              </div>
              <div
                className={`rounded-xl p-3 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
              >
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Mail
                    className={`h-3 w-3 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                  />
                  <span
                    className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Nieuw
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className={`h-2 w-2 rounded-full ${isDark ? "bg-blue-400" : "bg-blue-500"}`}
                  />
                  <span
                    className={`text-xl font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    {requestStats.new}
                  </span>
                </div>
              </div>
              <div
                className={`rounded-xl p-3 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
              >
                <div className="flex items-center gap-1.5 mb-1.5">
                  <CheckCircle2
                    className={`h-3 w-3 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                  />
                  <span
                    className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Afgerond
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className={`h-2 w-2 rounded-full ${isDark ? "bg-emerald-400" : "bg-emerald-500"}`}
                  />
                  <span
                    className={`text-xl font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    {requests.filter((r) => r.status === "completed").length}
                  </span>
                </div>
              </div>
            </div>

            {selectedRequest ? (
              /* ─── Request Detail View ─── */
              <div
                className={`rounded-xl border ${isDark ? "border-white/[0.06] bg-white/[0.02]" : "border-gray-100 bg-white"}`}
              >
                <div className="p-6 pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setSelectedRequest(null)}
                        className={`transition-colors ${isDark ? "text-gray-500 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
                      >
                        <ArrowLeft className="h-5 w-5" />
                      </button>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3
                            className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                          >
                            {selectedRequest.configurator_name}
                          </h3>
                          {selectedRequest.type &&
                            selectedRequest.type !== "request" && (
                              <span
                                className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                                  selectedRequest.type === "saved"
                                    ? isDark
                                      ? "bg-amber-500/10 text-amber-400"
                                      : "bg-amber-100 text-amber-700"
                                    : isDark
                                      ? "bg-purple-500/10 text-purple-400"
                                      : "bg-purple-100 text-purple-700"
                                }`}
                              >
                                {selectedRequest.type === "saved"
                                  ? "Opgeslagen"
                                  : "Betaling"}
                              </span>
                            )}
                        </div>
                        <p
                          className={`text-sm ${isDark ? "text-gray-500" : "text-gray-500"}`}
                        >
                          {formatDate(selectedRequest.created_at)} —{" "}
                          {selectedRequest.contact_name}
                        </p>
                        {selectedRequest.payment_status && (
                          <p
                            className={`text-xs mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                          >
                            Betaling: {selectedRequest.payment_status}
                            {selectedRequest.payment_amount > 0 &&
                              ` — €${Number(selectedRequest.payment_amount).toFixed(2)}`}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={selectedRequest.status}
                        onChange={(e) =>
                          handleStatusChange(selectedRequest.id, e.target.value)
                        }
                        className={`text-sm border rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 ${isDark ? "border-white/[0.06] bg-white/[0.04] text-white focus:ring-white/10" : "border-gray-200 bg-white text-gray-900 focus:ring-gray-200"}`}
                      >
                        {Object.entries(statusConfig).map(
                          ([key, { label }]) => (
                            <option key={key} value={key}>
                              {label}
                            </option>
                          ),
                        )}
                      </select>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`h-8 w-8 ${isDark ? "text-gray-500 hover:text-red-400 hover:bg-red-500/10" : "text-gray-500 hover:text-red-600 hover:bg-red-50"}`}
                        onClick={() =>
                          handleDeleteRequest(
                            selectedRequest.id,
                            selectedRequest.contact_name,
                          )
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="px-6 pb-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Contact Info */}
                    <div className="space-y-4">
                      <h3
                        className={`font-semibold flex items-center gap-2 ${isDark ? "text-white" : "text-gray-900"}`}
                      >
                        <User
                          className={`h-4 w-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                        />
                        Contactgegevens
                      </h3>
                      <div
                        className={`rounded-lg p-4 space-y-3 ${isDark ? "bg-white/[0.03]" : "bg-gray-50"}`}
                      >
                        <div className="flex items-center gap-3">
                          <User
                            className={`h-4 w-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                          />
                          <span
                            className={`text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}
                          >
                            {selectedRequest.contact_name}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <Mail
                            className={`h-4 w-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                          />
                          <a
                            href={`mailto:${selectedRequest.contact_email}`}
                            className={`text-sm hover:underline ${isDark ? "text-white" : "text-gray-900"}`}
                          >
                            {selectedRequest.contact_email}
                          </a>
                        </div>
                        {selectedRequest.contact_phone && (
                          <div className="flex items-center gap-3">
                            <Phone
                              className={`h-4 w-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                            />
                            <a
                              href={`tel:${selectedRequest.contact_phone}`}
                              className={`text-sm hover:underline ${isDark ? "text-white" : "text-gray-900"}`}
                            >
                              {selectedRequest.contact_phone}
                            </a>
                          </div>
                        )}
                        {selectedRequest.contact_message && (
                          <div
                            className={`flex items-start gap-3 pt-2 border-t ${isDark ? "border-white/[0.06]" : "border-gray-200"}`}
                          >
                            <MessageSquare
                              className={`h-4 w-4 mt-0.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                            />
                            <p
                              className={`text-sm whitespace-pre-wrap ${isDark ? "text-gray-400" : "text-gray-700"}`}
                            >
                              {selectedRequest.contact_message}
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            window.open(
                              `mailto:${selectedRequest.contact_email}`,
                            )
                          }
                          className={`${isDark ? "border-white/[0.08] text-gray-300 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                        >
                          <Mail className="h-4 w-4 mr-2" />
                          Beantwoorden
                        </Button>
                        {selectedRequest.contact_phone && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              window.open(
                                `tel:${selectedRequest.contact_phone}`,
                              )
                            }
                            className={`${isDark ? "border-white/[0.08] text-gray-300 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                          >
                            <Phone className="h-4 w-4 mr-2" />
                            Bellen
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Selections */}
                    <div className="space-y-4">
                      <h3
                        className={`font-semibold flex items-center gap-2 ${isDark ? "text-white" : "text-gray-900"}`}
                      >
                        <SlidersHorizontal
                          className={`h-4 w-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                        />
                        Configuratie keuzes
                      </h3>
                      <div
                        className={`border rounded-lg overflow-hidden ${isDark ? "border-white/[0.06]" : "border-gray-200"}`}
                      >
                        <Table>
                          <TableHeader>
                            <TableRow
                              className={
                                isDark ? "bg-white/[0.02]" : "bg-gray-50"
                              }
                            >
                              <TableHead
                                className={`text-xs font-semibold ${isDark ? "text-gray-500" : "text-gray-600"}`}
                              >
                                Stap
                              </TableHead>
                              <TableHead
                                className={`text-xs font-semibold ${isDark ? "text-gray-500" : "text-gray-600"}`}
                              >
                                Keuze
                              </TableHead>
                              <TableHead
                                className={`text-xs font-semibold text-right ${isDark ? "text-gray-500" : "text-gray-600"}`}
                              >
                                Prijs
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(selectedRequest.selections || []).map(
                              (sel, i) => (
                                <TableRow
                                  key={i}
                                  className={
                                    isDark ? "border-white/[0.04]" : ""
                                  }
                                >
                                  <TableCell
                                    className={`text-sm ${isDark ? "text-gray-500" : "text-gray-500"}`}
                                  >
                                    {sel.stepName}
                                  </TableCell>
                                  <TableCell
                                    className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                                  >
                                    {sel.optionName}
                                  </TableCell>
                                  <TableCell
                                    className={`text-sm text-right ${isDark ? "text-gray-400" : "text-gray-700"}`}
                                  >
                                    {sel.price > 0
                                      ? `${sel.priceType === "addition" ? "+" : ""}€${Number(sel.price).toFixed(2)}`
                                      : "—"}
                                  </TableCell>
                                </TableRow>
                              ),
                            )}
                          </TableBody>
                        </Table>
                        <div
                          className={`px-4 py-3 border-t flex justify-between items-center ${isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-gray-50 border-gray-200"}`}
                        >
                          <span
                            className={`text-sm font-bold ${isDark ? "text-white" : "text-gray-900"}`}
                          >
                            Totaal
                          </span>
                          <span
                            className={`text-lg font-bold ${isDark ? "text-white" : "text-gray-900"}`}
                          >
                            €
                            {Number(selectedRequest.total_price || 0).toFixed(
                              2,
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* ─── Requests List ─── */
              <>
                {/* Mobile Search Block */}
                <div
                  className={`sm:hidden rounded-xl border p-4 ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-100"}`}
                >
                  <div className="relative w-full">
                    <Search
                      className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                    />
                    <Input
                      placeholder="Zoeken..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={`pl-9 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "border-gray-200"}`}
                    />
                  </div>
                </div>

                {/* Mobile Request Cards */}
                {requestsLoading ? (
                  <div
                    className={`sm:hidden rounded-xl border p-12 text-center ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-100"}`}
                  >
                    <div className="flex justify-center">
                      <div
                        className={`w-8 h-8 rounded-full border-2 animate-spin ${isDark ? "border-white/[0.08] border-t-white/40" : "border-gray-200 border-t-gray-600"}`}
                      />
                    </div>
                  </div>
                ) : requests.length === 0 ? (
                  <div
                    className={`sm:hidden rounded-xl border p-12 text-center ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-100"}`}
                  >
                    <div
                      className={`mx-auto h-12 w-12 rounded-full flex items-center justify-center mb-3 ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
                    >
                      <Inbox
                        className={`h-6 w-6 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                      />
                    </div>
                    <h3
                      className={`text-[14px] font-semibold mb-1 ${isDark ? "text-white" : "text-gray-900"}`}
                    >
                      Nog geen aanvragen
                    </h3>
                    <p
                      className={`text-[13px] max-w-md mx-auto ${isDark ? "text-gray-500" : "text-gray-500"}`}
                    >
                      Zodra klanten je configurator invullen, verschijnen hun
                      aanvragen hier.
                    </p>
                  </div>
                ) : (
                  <div className="sm:hidden space-y-3">
                    {requests
                      .filter(
                        (r) =>
                          !searchTerm ||
                          (r.contact_name || "")
                            .toLowerCase()
                            .includes(searchTerm.toLowerCase()) ||
                          (r.configurator_name || "")
                            .toLowerCase()
                            .includes(searchTerm.toLowerCase()) ||
                          (r.contact_email || "")
                            .toLowerCase()
                            .includes(searchTerm.toLowerCase()),
                      )
                      .map((request, i) => (
                        <motion.div
                          key={request.id}
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.02 }}
                          className={`rounded-xl overflow-hidden cursor-pointer ${
                            request.status === "new"
                              ? isDark
                                ? "bg-white/[0.04] border border-blue-500/20"
                                : "bg-blue-50/30 border border-blue-100"
                              : isDark
                                ? "bg-white/[0.03] border border-white/[0.06]"
                                : "bg-white border border-gray-200/80"
                          }`}
                          onClick={() => {
                            setSelectedRequest(request);
                            if (request.status === "new")
                              handleStatusChange(request.id, "read");
                          }}
                        >
                          <div className="p-3 flex items-center gap-3">
                            <div
                              className={`h-11 w-11 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
                            >
                              {request.status === "new" ? (
                                <div className="relative">
                                  <Mail
                                    className={`h-5 w-5 ${isDark ? "text-blue-400" : "text-blue-600"}`}
                                  />
                                  <div className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-blue-500" />
                                </div>
                              ) : (
                                <User
                                  className={`h-5 w-5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                                />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3
                                className={`text-[13px] truncate ${request.status === "new" ? "font-bold" : "font-semibold"} ${isDark ? "text-white" : "text-gray-900"}`}
                              >
                                {request.contact_name}
                              </h3>
                              <span
                                className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                              >
                                {request.configurator_name}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span
                                className={`text-[13px] font-bold ${isDark ? "text-white" : "text-gray-900"}`}
                              >
                                €{Number(request.total_price || 0).toFixed(2)}
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteRequest(
                                    request.id,
                                    request.contact_name,
                                  );
                                }}
                                className={`h-8 w-8 rounded-lg inline-flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-red-400 hover:bg-red-500/10" : "text-gray-400 hover:text-red-600 hover:bg-red-50"}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                  </div>
                )}

                {/* Desktop: Search & Table in one wrapper */}
                <div
                  className={`hidden sm:block rounded-xl border ${isDark ? "border-white/[0.06] bg-white/[0.02]" : "border-gray-100 bg-white"}`}
                >
                  <div className="p-6 pb-4">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <h3
                          className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                        >
                          Alle Aanvragen
                        </h3>
                        <p
                          className={`text-sm ${isDark ? "text-gray-500" : "text-gray-500"}`}
                        >
                          Bekijk en beheer configurator aanvragen van klanten
                        </p>
                      </div>
                      <div className="relative w-64">
                        <Search
                          className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                        />
                        <Input
                          placeholder="Zoeken..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className={`pl-9 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "border-gray-200"}`}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="px-6 pb-6">
                    {requestsLoading ? (
                      <div className="flex justify-center items-center py-16">
                        <div
                          className={`w-8 h-8 rounded-full border-2 animate-spin ${isDark ? "border-white/[0.08] border-t-white/40" : "border-gray-200 border-t-gray-600"}`}
                        />
                      </div>
                    ) : requests.length === 0 ? (
                      <div className="text-center py-16">
                        <div
                          className={`mx-auto h-16 w-16 rounded-full flex items-center justify-center mb-4 ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
                        >
                          <Inbox
                            className={`h-8 w-8 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                          />
                        </div>
                        <h3
                          className={`text-lg font-medium mb-2 ${isDark ? "text-white" : "text-gray-900"}`}
                        >
                          Nog geen aanvragen
                        </h3>
                        <p
                          className={`max-w-md mx-auto ${isDark ? "text-gray-500" : "text-gray-500"}`}
                        >
                          Zodra klanten je configurator invullen, verschijnen
                          hun aanvragen hier.
                        </p>
                      </div>
                    ) : (
                      <div
                        className={`rounded-md border overflow-hidden ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
                      >
                        <Table>
                          <TableHeader>
                            <TableRow
                              className={
                                isDark
                                  ? "bg-white/[0.02] border-white/[0.06]"
                                  : "bg-gray-50/50"
                              }
                            >
                              <TableHead
                                className={`font-semibold ${isDark ? "text-gray-500" : "text-gray-700"}`}
                              >
                                Klant
                              </TableHead>
                              <TableHead
                                className={`font-semibold ${isDark ? "text-gray-500" : "text-gray-700"}`}
                              >
                                Configurator
                              </TableHead>
                              <TableHead
                                className={`font-semibold text-right ${isDark ? "text-gray-500" : "text-gray-700"}`}
                              >
                                Totaal
                              </TableHead>
                              <TableHead
                                className={`font-semibold ${isDark ? "text-gray-500" : "text-gray-700"}`}
                              >
                                Status
                              </TableHead>
                              <TableHead
                                className={`font-semibold ${isDark ? "text-gray-500" : "text-gray-700"}`}
                              >
                                Datum
                              </TableHead>
                              <TableHead
                                className={`font-semibold text-right ${isDark ? "text-gray-500" : "text-gray-700"}`}
                              >
                                Acties
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {requests
                              .filter(
                                (r) =>
                                  !searchTerm ||
                                  (r.contact_name || "")
                                    .toLowerCase()
                                    .includes(searchTerm.toLowerCase()) ||
                                  (r.configurator_name || "")
                                    .toLowerCase()
                                    .includes(searchTerm.toLowerCase()) ||
                                  (r.contact_email || "")
                                    .toLowerCase()
                                    .includes(searchTerm.toLowerCase()),
                              )
                              .map((request) => (
                                <TableRow
                                  key={request.id}
                                  className={`cursor-pointer transition-colors ${
                                    request.status === "new"
                                      ? isDark
                                        ? "bg-white/[0.04] hover:bg-white/[0.06]"
                                        : "bg-blue-50/30 hover:bg-blue-50/60"
                                      : isDark
                                        ? "hover:bg-white/[0.02]"
                                        : "hover:bg-gray-50/50"
                                  } ${isDark ? "border-white/[0.04]" : ""}`}
                                  onClick={() => {
                                    setSelectedRequest(request);
                                    if (request.status === "new") {
                                      handleStatusChange(request.id, "read");
                                    }
                                  }}
                                >
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      {request.status === "new" && (
                                        <div className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" />
                                      )}
                                      <div>
                                        <p
                                          className={`text-sm ${request.status === "new" ? "font-semibold" : "font-medium"} ${isDark ? "text-white" : "text-gray-900"}`}
                                        >
                                          {request.contact_name}
                                        </p>
                                        <p
                                          className={`text-xs ${isDark ? "text-gray-500" : "text-gray-500"}`}
                                        >
                                          {request.contact_email}
                                        </p>
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell
                                    className={`text-sm ${isDark ? "text-gray-400" : "text-gray-700"}`}
                                  >
                                    {request.configurator_name}
                                  </TableCell>
                                  <TableCell
                                    className={`text-sm font-semibold text-right ${isDark ? "text-white" : "text-gray-900"}`}
                                  >
                                    €
                                    {Number(request.total_price || 0).toFixed(
                                      2,
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-1.5">
                                      <Badge
                                        className={`${statusConfig[request.status]?.className || "bg-gray-100 text-gray-700"} hover:opacity-80`}
                                      >
                                        {statusConfig[request.status]?.label ||
                                          request.status}
                                      </Badge>
                                      {request.type === "saved" && (
                                        <Badge
                                          className={`${isDark ? "bg-amber-500/10 text-amber-400" : "bg-amber-100 text-amber-700"} hover:opacity-80 text-[10px]`}
                                        >
                                          Opgeslagen
                                        </Badge>
                                      )}
                                      {request.type === "payment" && (
                                        <Badge
                                          className={`${isDark ? "bg-purple-500/10 text-purple-400" : "bg-purple-100 text-purple-700"} hover:opacity-80 text-[10px]`}
                                        >
                                          Betaling
                                        </Badge>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell
                                    className={`text-sm ${isDark ? "text-gray-500" : "text-gray-500"}`}
                                  >
                                    <div className="flex items-center gap-1">
                                      <Clock className="h-3.5 w-3.5" />
                                      {formatDate(request.created_at)}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <div
                                      className="flex items-center justify-end space-x-1"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className={`h-8 w-8 ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"}`}
                                        onClick={() => {
                                          setSelectedRequest(request);
                                          if (request.status === "new") {
                                            handleStatusChange(
                                              request.id,
                                              "read",
                                            );
                                          }
                                        }}
                                        title="Bekijken"
                                      >
                                        <Eye className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className={`h-8 w-8 ${isDark ? "text-gray-500 hover:text-red-400 hover:bg-red-500/10" : "text-gray-500 hover:text-red-600 hover:bg-red-50"}`}
                                        onClick={() =>
                                          handleDeleteRequest(
                                            request.id,
                                            request.contact_name,
                                          )
                                        }
                                        title="Verwijderen"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default ConfiguratorManager;
